import React, { ChangeEvent, forwardRef, useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import classNames from "classnames";

import { ISelectField } from "types";
import { FormLabel } from "components";
import { useOnPersistedPageShow } from "utils";
import { ChevronDown } from "assets/icons";

import { FormText } from "../components";

interface SelectFieldProps extends ISelectField {
  error?: FieldError | null;
}

export const SelectField = forwardRef<
  HTMLSelectElement,
  SelectFieldProps & React.InputHTMLAttributes<HTMLSelectElement>
>(
  (
    {
      name,
      value,
      label,
      labelIcon,
      hideLabel,
      prefixText,
      error,
      required,
      options,
      onChange,
      className = "",
    },
    ref
  ) => {
    const [selectedLabel, setSelectedLabel] = useState<string | undefined>(); // New state to track the selected label
    const [selected, setSelected] = useState(false);

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
      const currentValue = (event.target as HTMLSelectElement).value;

      const selectedOption = options.find(
        (option) => option.value === currentValue
      );
      setSelectedLabel(selectedOption ? selectedOption.label : ""); // Update label based on selection

      setSelected(!!currentValue);
      onChange && onChange(event);
    };

    // bfcache fix
    useOnPersistedPageShow(() => setSelected(false));

    useEffect(() => {
      if (!value) return;

      const selectedOption = options.find((option) => option.value === value);
      setSelected(!!selectedOption?.value);
      setSelectedLabel(selectedOption?.label); // Set the label for the selected value
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <div className={`field__wrapper ${className}`}>
        {prefixText && <FormText label={prefixText} />}
        <div className="field field--select">
          <label className="field__label" htmlFor={name}>
            <FormLabel
              label={label}
              hideLabel={hideLabel}
              required={required}
            />
            <div
              className={classNames("form-select", [
                error?.message && "is-error",
                selected && "is-selected",
              ])}
            >
              {hideLabel && labelIcon && typeof labelIcon === "string" && (
                <span
                  className="form-select__icon"
                  dangerouslySetInnerHTML={{ __html: labelIcon }}
                />
              )}

              {hideLabel && labelIcon && typeof labelIcon !== "string" && (
                <span className="form-select__icon">{labelIcon}</span>
              )}

              <div className="form-select__select-container">
                <select
                  id={name}
                  ref={ref}
                  name={name}
                  aria-required={required}
                  value={value}
                  onChange={handleChange}
                  className="form-select__select"
                >
                  {options.map(({ label, value, ...rest }) => (
                    <option key={value} value={value} {...rest}>
                      {label}
                    </option>
                  ))}
                </select>
                {!selected && (
                  <div className="form-select__fake-select">{label}</div>
                )}
                {selected && (
                  <div className="form-select__fake-select form-select__fake-select--active">
                    {label && `${label}:`}{" "}
                    <span className="current-select">{selectedLabel}</span>
                  </div>
                )}
              </div>

              <span className="form-select__arrow">
                <ChevronDown />
              </span>
            </div>
          </label>
          {error?.message && (
            <span className="form-message is-error is-animated" role="alert">
              {error?.message}
            </span>
          )}
        </div>
      </div>
    );
  }
);
